import { useState, useEffect, useRef } from 'react';
import { GlobalTokenStyles } from '@seed-health/tokens/react';
import Head from 'next/head';
import EventListeners from 'lib/events';
import {
  withLayoutSelector,
  type NextPageWithLayout,
} from 'components/layout/layouts';
import { UserProvider } from 'context-providers/user-provider';
import { CartProvider } from 'context-providers/cart-provider';
import { SnackbarProvider } from 'context-providers/snackbar-provider';
import { ProductsProvider } from 'context-providers/products-provider';
import { LocationProvider } from 'context-providers/location-provider';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import Analytics from 'components/misc/analytics';
import { FeatureFlagProvider } from 'context-providers/feature-flag-provider';
import { initializeDatadogRUM } from 'lib/datadog-rum';
import { initializeConsentReload } from 'lib/consent-reload';
import { initializeShopifyConsentBridge } from 'lib/cookie-consent';
import { initClientIp } from 'lib/client-ip';
import dynamic from 'next/dynamic';
import ErrorBoundary from 'components/misc/error-boundary';

import 'styles/app.scss';
import { useRouter } from 'next/router';
import { useExperimentRouting } from 'hooks/use-experiment-routing';
import ChatWidget from 'components/chat';

const FeatureFlagPanel = dynamic(
  () => import('components/misc/feature-flag-panel'),
);

// Sanity Presentation overlays; loads only in Next draft mode.
const SanityVisualEditing = dynamic(() =>
  import('@sanity/visual-editing/next-pages-router').then(
    (mod) => mod.VisualEditing,
  ),
);

type Props = {
  pageProps: any;
  Component: NextPageWithLayout<any>;
  err: any;
  router: any;
};

const App = (props: Props) => {
  const { Component, pageProps, err, ...appProps } = props;
  const [queryClient] = useState(() => new QueryClient());
  const [isClient, setIsClient] = useState<boolean>(false);
  const router = useRouter();

  useExperimentRouting();

  // Pages opt into their own layout; everything else gets the standard chrome.
  const getLayout = Component.getLayout ?? withLayoutSelector;

  useEffect(() => {
    // Initialize Datadog RUM
    initializeDatadogRUM();
    initializeConsentReload();
    initializeShopifyConsentBridge();
    initClientIp();

    window.FBQ_SETUP = () => {
      return;
    };

    EventListeners.Load();
  }, []);

  const previousRoute = useRef<string>('');

  const cleanEmbeddablesCss = () => {
    const styleElements = document.querySelectorAll('head style');

    styleElements.forEach((styleElement) => {
      if (styleElement.textContent?.includes('background-color: unset')) {
        console.log('Found embeddables CSS, removing:', styleElement);
        styleElement.remove();
      }
    });
  };

  useEffect(() => {
    previousRoute.current = router.asPath;
    setIsClient(true);
    // Root-level mount effects run only after the entire page tree's initial
    // hydration (or hydration-recovery) commit. The Chromatic visual tests
    // wait on this flag before interacting with the page — scrolling earlier
    // can flip IntersectionObserver state mid-hydration and trigger React
    // #418 (playwright/utils/helpers/visual-snapshot-helpers.ts).
    window.__APP_HYDRATED__ = true;
  }, []);

  useEffect(() => {
    // Temporary hack to force full page load for ds01 quizzes
    const fullReloadQuizPaths = [
      '/is-ds01-right-for-you',
      '/is-ds01-right-for-you-glp1',
    ];
    const handleRouteChangeStart = (url: string) => {
      if (fullReloadQuizPaths.includes(url)) {
        window.location.href = url;
        return false;
      }
    };

    const handleRouteChangeComplete = (url: string) => {
      if (fullReloadQuizPaths.includes(previousRoute.current)) {
        cleanEmbeddablesCss();
      }
      previousRoute.current = url;
    };

    router.events.on('routeChangeComplete', handleRouteChangeComplete);
    router.events.on('routeChangeStart', handleRouteChangeStart);
    return () => {
      router.events.off('routeChangeComplete', handleRouteChangeComplete);
      router.events.off('routeChangeStart', handleRouteChangeStart);
    };
  }, [router.events]);

  return (
    <>
      <Head>
        <>
          <meta
            name='viewport'
            content='width=device-width, height=device-height, initial-scale=1'
          />
          <meta httpEquiv='Content-Type' content='text/html; charset=utf-8' />
        </>
      </Head>
      <ErrorBoundary appProps={appProps}>
        <QueryClientProvider client={queryClient}>
          <FeatureFlagProvider>
            {process.env.NEXT_PUBLIC_STAGING === 'true' && isClient ? (
              <FeatureFlagPanel />
            ) : null}
            <LocationProvider>
              <ProductsProvider
                products={pageProps.products}
                shopifyProducts={pageProps.shopifyProducts}
                refillProducts={pageProps.refillProducts}
              >
                <SnackbarProvider>
                  <UserProvider>
                    <CartProvider>
                      <Analytics />
                      <GlobalTokenStyles />
                      {router.isPreview && <SanityVisualEditing />}
                      {process.env.NEXT_PUBLIC_DG_CHAT_WIDGET_ID && (
                        <ChatWidget />
                      )}
                      {/* Page errors are caught here so the SiteDown fallback
                          renders with the providers its chrome depends on. */}
                      <ErrorBoundary
                        appProps={appProps}
                        resetKey={router.asPath}
                      >
                        {getLayout(
                          <Component {...pageProps} err={err} />,
                          pageProps,
                        )}
                      </ErrorBoundary>
                    </CartProvider>
                  </UserProvider>
                </SnackbarProvider>
              </ProductsProvider>
            </LocationProvider>
          </FeatureFlagProvider>
        </QueryClientProvider>
      </ErrorBoundary>
    </>
  );
};

export default App;
